import type { GetServerSideProps, NextPage } from "next";
import getAppSettings, { AppSettings } from "../lib/get-app-settings";
import CompanyStandardPage, {
  getServerSideProps as getStandardPageProps,
  type TCompanyStandardPageProps,
} from "./_companySlug.standard";
import CompanyLightPage, {
  getServerSideProps as getLightPageProps,
  type TCompanyLightPageProps,
} from "./_companySlug.light";
import {
  THelpCenterLightLayout,
  THelpCenterStandardLayout,
  HelpCenterLayout,
} from "../lib/load-company-settings";
import authenticateViaUrlParameters from "../lib/authenticate-via-url-parameters";

export type TCompanyPageProps =
  | TCompanyStandardPageProps
  | TCompanyLightPageProps;

const CompanyPage: NextPage<TCompanyPageProps> = ({
  appSettings,
  ...props
}) => {
  if (appSettings.layout.startsWith("light_")) {
    return (
      <CompanyLightPage
        {...(props as TCompanyLightPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterLightLayout }
        }
      />
    );
  } else {
    return (
      <CompanyStandardPage
        {...(props as TCompanyStandardPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterStandardLayout }
        }
      />
    );
  }
};

export default CompanyPage;

export const getServerSideProps: GetServerSideProps<TCompanyPageProps> = async (
  context
) => {
  // this is nearly always the first function
  // to be called when the user opens the help center
  // since CNAME -> is mapped to -> /[companySlug]
  const appSettings = await getAppSettings(context);

  if (!appSettings) {
    return { redirect: { statusCode: 302, destination: "/" } };
  }

  const authResponse = await authenticateViaUrlParameters(appSettings, context);
  if (authResponse) return authResponse;

  const previewLayout = context.query.preview_layout?.toString();
  if (
    previewLayout?.match(
      /^(light_sidebar|standard_(condensed|horizontal|grid))$/
    )
  ) {
    appSettings.layout = previewLayout as HelpCenterLayout;
  }

  if (appSettings.layout.startsWith("light_")) {
    return getLightPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterLightLayout }
    );
  } else {
    return getStandardPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterStandardLayout }
    );
  }
};
